<template>
  <!-- Add -->
  <div class="modal fade dq-asset-modal" id="add_asset" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-lg dq-asset-dialog">
      <div class="modal-content dq-modal-content" :key="addKey">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-indigo">
              <i class="ti ti-device-laptop"></i>
            </div>
            <div>
              <h4 class="modal-title dq-title mb-0">Add Asset</h4>
              <div class="dq-subtitle">
                Track company equipment, assignments, warranty and depreciation.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitCreate">
          <div class="modal-body dq-body pb-0">
            <!-- Sticky summary row -->
            <div class="dq-summary mb-3">
              <div class="dq-summary-left">
                <div class="dq-summary-title">
                  <i class="ti ti-info-circle me-1"></i> Summary
                </div>
                <div class="dq-summary-sub">
                  {{ form.name || "— Asset name" }}
                  <span class="dq-dot">•</span>
                  {{ categoryLabel(form.category_id) || "— Category" }}
                  <span class="dq-dot">•</span>
                  <span class="dq-pill" :class="pillClass(form.status)">
                    <i class="ti ti-bolt"></i> {{ form.status || "Active" }}
                  </span>
                </div>
              </div>

              <div class="dq-summary-right">
                <span class="dq-mini">
                  <i class="ti ti-user"></i>
                  {{ assigneeLabel(form.assigned_to_id) || "Unassigned" }}
                </span>
                <span class="dq-mini">
                  <i class="ti ti-map-pin"></i>
                  {{ form.location || "—" }}
                </span>
              </div>
            </div>

            <div class="row g-3">
              <!-- LEFT -->
              <div class="col-lg-6">
                <div class="dq-card">
                  <div class="dq-card-head">
                    <span class="dq-badge dq-badge-indigo"><i class="ti ti-box"></i></span>
                    <div>
                      <div class="dq-h6">Asset Identity</div>
                      <div class="dq-help">Core details used to identify the asset.</div>
                    </div>
                  </div>

                  <div class="dq-card-body">
                    <div class="mb-3">
                      <label class="form-label dq-label">
                        Asset Name <span class="text-danger">*</span>
                      </label>
                      <input
                        v-model.trim="form.name"
                        class="form-control dq-input"
                        placeholder="e.g. Dell Latitude 5520"
                      />
                      <div class="dq-hint">Use a clear model name employees recognize.</div>
                    </div>

                    <div class="mb-3">
                      <label class="form-label dq-label">
                        Category <span class="text-danger">*</span>
                      </label>
                      <select v-model="form.category_id" class="form-select dq-input">
                        <option :value="null" disabled>Select</option>
                        <option v-for="c in categoryStore.items" :key="c.id" :value="c.id">
                          {{ c.name }}
                        </option>
                      </select>
                    </div>

                    <div class="row g-3">
                      <div class="col-md-6">
                        <label class="form-label dq-label">Asset Tag</label>
                        <input
                          v-model.trim="form.asset_tag"
                          class="form-control dq-input"
                          placeholder="e.g. TAG-1001"
                        />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label dq-label">Serial Number</label>
                        <input
                          v-model.trim="form.serial_number"
                          class="form-control dq-input"
                          placeholder="e.g. SN-ABC-12345"
                        />
                      </div>
                    </div>

                    <div class="row g-3 mt-0">
                      <div class="col-md-6">
                        <label class="form-label dq-label">Manufacturer</label>
                        <input
                          v-model.trim="form.manufacturer"
                          class="form-control dq-input"
                          placeholder="e.g. Dell"
                        />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label dq-label">Model</label>
                        <input
                          v-model.trim="form.model"
                          class="form-control dq-input"
                          placeholder="e.g. Latitude 5520"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- RIGHT -->
              <div class="col-lg-6">
                <div class="dq-card">
                  <div class="dq-card-head">
                    <span class="dq-badge dq-badge-blue"><i class="ti ti-receipt"></i></span>
                    <div>
                      <div class="dq-h6">Purchase & Assignment</div>
                      <div class="dq-help">Cost, supplier, warranty and who owns it.</div>
                    </div>
                  </div>

                  <div class="dq-card-body">
                    <div class="row g-3">
                      <div class="col-md-6">
                        <label class="form-label dq-label">Purchase Date</label>
                        <input v-model="form.purchase_date" type="date" class="form-control dq-input" />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label dq-label">Warranty End Date</label>
                        <input v-model="form.warranty_end_date" type="date" class="form-control dq-input" />
                      </div>
                    </div>

                    <div class="row g-3 mt-0">
                      <div class="col-md-6">
                        <label class="form-label dq-label">Purchase Cost</label>
                        <div class="input-group dq-inputgroup">
                          <span class="input-group-text dq-addon">
                            <i class="ti ti-currency-dollar"></i>
                          </span>
                          <input
                            v-model.number="form.purchase_cost"
                            type="number"
                            step="0.01"
                            class="form-control dq-input"
                            placeholder="e.g. 125000"
                          />
                        </div>
                      </div>
                      <div class="col-md-6">
                        <label class="form-label dq-label">Supplier</label>
                        <input
                          v-model.trim="form.supplier"
                          class="form-control dq-input"
                          placeholder="e.g. Tech Distributors Ltd."
                        />
                      </div>
                    </div>

                    <div class="row g-3 mt-0">
                      <div class="col-md-6">
                        <label class="form-label dq-label">Assigned To</label>
                        <select v-model="form.assigned_to_id" class="form-select dq-input">
                          <option :value="null">Unassigned</option>
                          <option v-for="u in userStore.users" :key="u.id" :value="u.id">
                            {{ u.first_name }} {{ u.last_name }}
                          </option>
                        </select>
                      </div>
                      <div class="col-md-6">
                        <label class="form-label dq-label">Status</label>
                        <select v-model="form.status" class="form-select dq-input">
                          <option>Active</option>
                          <option>Inactive</option>
                          <option>Maintenance</option>
                          <option>Disposed</option>
                        </select>
                      </div>
                    </div>

                    <div class="row g-3 mt-0">
                      <div class="col-md-6">
                        <label class="form-label dq-label">Condition</label>
                        <select v-model="form.condition" class="form-select dq-input">
                          <option>New</option>
                          <option>Good</option>
                          <option>Fair</option>
                          <option>Poor</option>
                        </select>
                      </div>
                      <div class="col-md-6">
                        <label class="form-label dq-label">Location</label>
                        <input
                          v-model.trim="form.location"
                          class="form-control dq-input"
                          placeholder="e.g. HQ 3rd Floor, IT Store"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Depreciation -->
                <div class="dq-card mt-3">
                  <div class="dq-card-head">
                    <span class="dq-badge dq-badge-amber"><i class="ti ti-chart-bar"></i></span>
                    <div>
                      <div class="dq-h6">Depreciation</div>
                      <div class="dq-help">Optional accounting rules for this asset.</div>
                    </div>
                  </div>
                  <div class="dq-card-body">
                    <div class="row g-3 align-items-end">
                      <div class="col-md-6">
                        <label class="form-label dq-label">Method</label>
                        <select v-model="form.depreciation_method" class="form-select dq-input">
                          <option value="">None</option>
                          <option>Straight Line</option>
                          <option>Declining Balance</option>
                        </select>
                      </div>
                      <div class="col-md-6" v-if="form.depreciation_method">
                        <label class="form-label dq-label">Rate (%)</label>
                        <input
                          v-model.number="form.depreciation_rate"
                          type="number"
                          step="0.01"
                          class="form-control dq-input"
                          placeholder="e.g. 25"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Notes (Full width) -->
              <div class="col-12">
                <div class="dq-card">
                  <div class="dq-card-head">
                    <span class="dq-badge dq-badge-purple"><i class="ti ti-notes"></i></span>
                    <div>
                      <div class="dq-h6">Notes</div>
                      <div class="dq-help">Any extra context for this asset.</div>
                    </div>
                  </div>
                  <div class="dq-card-body">
                    <textarea
                      v-model.trim="form.notes"
                      class="form-control dq-input"
                      rows="3"
                      placeholder="Additional notes"
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-footer">
            <button
              type="button"
              class="btn btn-white border dq-btn dq-btn-muted me-2"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button type="submit" class="btn btn-primary dq-btn dq-btn-primary">
              <span v-if="busy" class="spinner-border spinner-border-sm me-2"></span>
              Add Asset
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Edit -->
  <div class="modal fade dq-asset-modal" id="edit_asset" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-lg dq-asset-dialog">
      <div class="modal-content dq-modal-content" :key="editKey">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-blue">
              <i class="ti ti-edit"></i>
            </div>
            <div>
              <h4 class="modal-title dq-title mb-0">Edit Asset</h4>
              <div class="dq-subtitle">Update details, assignment, status and depreciation.</div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitEdit">
          <div class="modal-body dq-body pb-0">
            <!-- SAME GRID / FIELDS as Add (bound to same form) -->
            <div class="dq-summary mb-3">
              <div class="dq-summary-left">
                <div class="dq-summary-title">
                  <i class="ti ti-info-circle me-1"></i> Summary
                </div>
                <div class="dq-summary-sub">
                  {{ form.name || "— Asset name" }}
                  <span class="dq-dot">•</span>
                  {{ categoryLabel(form.category_id) || "— Category" }}
                  <span class="dq-dot">•</span>
                  <span class="dq-pill" :class="pillClass(form.status)">
                    <i class="ti ti-bolt"></i> {{ form.status || "Active" }}
                  </span>
                </div>
              </div>

              <div class="dq-summary-right">
                <span class="dq-mini">
                  <i class="ti ti-user"></i>
                  {{ assigneeLabel(form.assigned_to_id) || "Unassigned" }}
                </span>
                <span class="dq-mini">
                  <i class="ti ti-map-pin"></i>
                  {{ form.location || "—" }}
                </span>
              </div>
            </div>

            <div class="row g-3">
              <!-- LEFT -->
              <div class="col-lg-6">
                <div class="dq-card">
                  <div class="dq-card-head">
                    <span class="dq-badge dq-badge-indigo"><i class="ti ti-box"></i></span>
                    <div>
                      <div class="dq-h6">Asset Identity</div>
                      <div class="dq-help">Core details used to identify the asset.</div>
                    </div>
                  </div>

                  <div class="dq-card-body">
                    <div class="mb-3">
                      <label class="form-label dq-label">
                        Asset Name <span class="text-danger">*</span>
                      </label>
                      <input v-model.trim="form.name" class="form-control dq-input" />
                    </div>

                    <div class="mb-3">
                      <label class="form-label dq-label">
                        Category <span class="text-danger">*</span>
                      </label>
                      <select v-model="form.category_id" class="form-select dq-input">
                        <option :value="null" disabled>Select</option>
                        <option v-for="c in categoryStore.items" :key="c.id" :value="c.id">
                          {{ c.name }}
                        </option>
                      </select>
                    </div>

                    <div class="row g-3">
                      <div class="col-md-6">
                        <label class="form-label dq-label">Asset Tag</label>
                        <input v-model.trim="form.asset_tag" class="form-control dq-input" />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label dq-label">Serial Number</label>
                        <input v-model.trim="form.serial_number" class="form-control dq-input" />
                      </div>
                    </div>

                    <div class="row g-3 mt-0">
                      <div class="col-md-6">
                        <label class="form-label dq-label">Manufacturer</label>
                        <input v-model.trim="form.manufacturer" class="form-control dq-input" />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label dq-label">Model</label>
                        <input v-model.trim="form.model" class="form-control dq-input" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- RIGHT -->
              <div class="col-lg-6">
                <div class="dq-card">
                  <div class="dq-card-head">
                    <span class="dq-badge dq-badge-blue"><i class="ti ti-receipt"></i></span>
                    <div>
                      <div class="dq-h6">Purchase & Assignment</div>
                      <div class="dq-help">Cost, supplier, warranty and who owns it.</div>
                    </div>
                  </div>

                  <div class="dq-card-body">
                    <div class="row g-3">
                      <div class="col-md-6">
                        <label class="form-label dq-label">Purchase Date</label>
                        <input v-model="form.purchase_date" type="date" class="form-control dq-input" />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label dq-label">Warranty End Date</label>
                        <input v-model="form.warranty_end_date" type="date" class="form-control dq-input" />
                      </div>
                    </div>

                    <div class="row g-3 mt-0">
                      <div class="col-md-6">
                        <label class="form-label dq-label">Purchase Cost</label>
                        <div class="input-group dq-inputgroup">
                          <span class="input-group-text dq-addon">
                            <i class="ti ti-currency-dollar"></i>
                          </span>
                          <input v-model.number="form.purchase_cost" type="number" step="0.01" class="form-control dq-input" />
                        </div>
                      </div>
                      <div class="col-md-6">
                        <label class="form-label dq-label">Supplier</label>
                        <input v-model.trim="form.supplier" class="form-control dq-input" />
                      </div>
                    </div>

                    <div class="row g-3 mt-0">
                      <div class="col-md-6">
                        <label class="form-label dq-label">Assigned To</label>
                        <select v-model="form.assigned_to_id" class="form-select dq-input">
                          <option :value="null">Unassigned</option>
                          <option v-for="u in userStore.users" :key="u.id" :value="u.id">
                            {{ u.first_name }} {{ u.last_name }}
                          </option>
                        </select>
                      </div>
                      <div class="col-md-6">
                        <label class="form-label dq-label">Status</label>
                        <select v-model="form.status" class="form-select dq-input">
                          <option>Active</option>
                          <option>Inactive</option>
                          <option>Maintenance</option>
                          <option>Disposed</option>
                        </select>
                      </div>
                    </div>

                    <div class="row g-3 mt-0">
                      <div class="col-md-6">
                        <label class="form-label dq-label">Condition</label>
                        <select v-model="form.condition" class="form-select dq-input">
                          <option>New</option>
                          <option>Good</option>
                          <option>Fair</option>
                          <option>Poor</option>
                        </select>
                      </div>
                      <div class="col-md-6">
                        <label class="form-label dq-label">Location</label>
                        <input v-model.trim="form.location" class="form-control dq-input" />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="dq-card mt-3">
                  <div class="dq-card-head">
                    <span class="dq-badge dq-badge-amber"><i class="ti ti-chart-bar"></i></span>
                    <div>
                      <div class="dq-h6">Depreciation</div>
                      <div class="dq-help">Optional accounting rules for this asset.</div>
                    </div>
                  </div>
                  <div class="dq-card-body">
                    <div class="row g-3 align-items-end">
                      <div class="col-md-6">
                        <label class="form-label dq-label">Method</label>
                        <select v-model="form.depreciation_method" class="form-select dq-input">
                          <option value="">None</option>
                          <option>Straight Line</option>
                          <option>Declining Balance</option>
                        </select>
                      </div>
                      <div class="col-md-6" v-if="form.depreciation_method">
                        <label class="form-label dq-label">Rate (%)</label>
                        <input v-model.number="form.depreciation_rate" type="number" step="0.01" class="form-control dq-input" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Notes -->
              <div class="col-12">
                <div class="dq-card">
                  <div class="dq-card-head">
                    <span class="dq-badge dq-badge-purple"><i class="ti ti-notes"></i></span>
                    <div>
                      <div class="dq-h6">Notes</div>
                      <div class="dq-help">Any extra context for this asset.</div>
                    </div>
                  </div>
                  <div class="dq-card-body">
                    <textarea v-model.trim="form.notes" class="form-control dq-input" rows="3"></textarea>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer dq-footer">
            <button type="button" class="btn btn-white border dq-btn dq-btn-muted me-2" data-bs-dismiss="modal">
              Cancel
            </button>
            <button type="submit" class="btn btn-primary dq-btn dq-btn-primary" :disabled="!isEdit">
              <span v-if="busy" class="spinner-border spinner-border-sm me-2"></span>
              Save Asset
              <i class="ti ti-check ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete (unchanged) -->
  <div class="modal fade" id="delete_modal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body text-center">
          <span class="avatar avatar-xl bg-transparent-danger text-danger mb-3">
            <i class="ti ti-trash-x fs-36"></i>
          </span>
          <h4 class="mb-1">Confirm Delete</h4>
          <p class="mb-3">Are you sure you want to delete this asset?</p>
          <div class="d-flex justify-content-center">
            <a href="javascript:void(0);" class="btn btn-light me-3" data-bs-dismiss="modal">Cancel</a>
            <button class="btn btn-danger" :disabled="deleting" @click="confirmDelete">
              <span v-if="deleting" class="spinner-border spinner-border-sm me-1"></span>
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useAssetStore } from "@/stores/assetStore";
import { useAssetCategoryStore } from "@/stores/assetCategoryStore";
import { useUserStore } from "@/stores/userStore";
import { toast } from "@/utils/toast";

const props = defineProps({ editItem: { type: Object, default: null } });
const emit = defineEmits(["saved"]);

const assetStore = useAssetStore();
const categoryStore = useAssetCategoryStore();
const userStore = useUserStore();

const deleting = ref(false);
const busy = computed(() => assetStore.loading);

// refresh keys
const addKey = ref(0);
const editKey = ref(0);

const form = ref(blank());
function blank() {
  return {
    name: "",
    category_id: null,
    asset_tag: "",
    serial_number: "",
    manufacturer: "",
    model: "",
    purchase_date: "",
    purchase_cost: null,
    supplier: "",
    warranty_end_date: "",
    assigned_to_id: null,
    status: "Active",
    location: "",
    condition: "Good",
    depreciation_method: "",
    depreciation_rate: null,
    notes: "",
  };
}
function resetForm() {
  const b = blank();
  for (const k in b) form.value[k] = b[k];
}

watch(
  () => props.editItem,
  (val) => {
    if (val) {
      Object.assign(form.value, {
        name: val.name ?? "",
        category_id: val.category_id ?? null,
        asset_tag: val.asset_tag ?? "",
        serial_number: val.serial_number ?? "",
        manufacturer: val.manufacturer ?? "",
        model: val.model ?? "",
        purchase_date: val.purchase_date ?? "",
        purchase_cost: val.purchase_cost ?? null,
        supplier: val.supplier ?? "",
        warranty_end_date: val.warranty_end_date ?? "",
        assigned_to_id: val.assigned_to_id ?? null,
        status: val.status ?? "Active",
        location: val.location ?? "",
        condition: val.condition ?? "Good",
        depreciation_method: val.depreciation_method ?? "",
        depreciation_rate: val.depreciation_rate ?? null,
        notes: val.notes ?? "",
      });
      editKey.value++;
    } else {
      resetForm();
    }
  },
  { immediate: true }
);

const isEdit = computed(() => !!props.editItem?.id);

async function submitCreate() {
  if (!form.value.name || !form.value.category_id) {
    toast("Name and Category are required", "error");
    return;
  }
  await assetStore.create({ ...form.value });
  closeNew();
  resetForm();
  addKey.value++;
  emit("saved");
}

async function submitEdit() {
  if (!props.editItem?.id) return;
  await assetStore.update(props.editItem.id, { ...form.value });
  closeEdit();
  resetForm();
  editKey.value++;
  emit("saved");
}

function closeNew() {
  document.querySelector('#add_asset [data-bs-dismiss="modal"]')?.click();
}
function closeEdit() {
  document.querySelector('#edit_asset [data-bs-dismiss="modal"]')?.click();
}

async function confirmDelete() {
  deleting.value = true;
  try {
    if (!props.editItem?.id) return;
    await assetStore.remove(props.editItem.id);
    emit("saved");
  } catch (e) {
    console.error(e);
  } finally {
    deleting.value = false;
  }
}

/* helpers for the summary header (UI only) */
function categoryLabel(id) {
  if (!id) return "";
  const c = (categoryStore.items || []).find((x) => Number(x.id) === Number(id));
  return c?.name || "";
}
function assigneeLabel(id) {
  if (!id) return "";
  const u = (userStore.users || []).find((x) => Number(x.id) === Number(id));
  return u ? `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() : "";
}
function pillClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "dq-pill-live";
  if (s === "maintenance") return "dq-pill-amber";
  if (s === "disposed") return "dq-pill-danger";
  return "dq-pill-draft";
}

let cleanup = null;
onMounted(async () => {
  await Promise.all([
    categoryStore.fetchAll({ per_page: 500 }),
    userStore.fetchUsers({ per_page: 500 }),
  ]);

  const addEl = document.getElementById("add_asset");
  const editEl = document.getElementById("edit_asset");

  const handleAddShow = () => {
    resetForm();
    addKey.value++;
  };
  const handleAddHidden = () => resetForm();
  const handleEditHidden = () => {
    resetForm();
    editKey.value++;
  };

  addEl?.addEventListener("show.bs.modal", handleAddShow);
  addEl?.addEventListener("hidden.bs.modal", handleAddHidden);
  editEl?.addEventListener("hidden.bs.modal", handleEditHidden);

  cleanup = () => {
    addEl?.removeEventListener("show.bs.modal", handleAddShow);
    addEl?.removeEventListener("hidden.bs.modal", handleAddHidden);
    editEl?.removeEventListener("hidden.bs.modal", handleEditHidden);
  };
});
onBeforeUnmount(() => cleanup && cleanup());
</script>

<style scoped>
/* Wider + premium shell */
.dq-asset-dialog {
  max-width: 1040px; /* wider than default modal-lg */
}
@media (max-width: 992px) {
  .dq-asset-dialog {
    max-width: 96%;
  }
}

.dq-modal-content {
  border-radius: 22px;
  border: none;
  overflow: hidden;
  box-shadow:
    0 10px 25px rgba(15, 23, 42, 0.08),
    0 40px 90px rgba(15, 23, 42, 0.14);
}

/* Header */
.dq-modal-header {
  padding: 18px 22px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.14), #fff);
}
.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-title {
  font-weight: 950;
  letter-spacing: -0.35px;
}
.dq-subtitle {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-close {
  opacity: 0.9;
}
.dq-close:hover {
  opacity: 1;
}

/* Hero icon */
.dq-hero {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.dq-hero-indigo {
  background: rgba(99, 102, 241, 0.16);
  color: rgb(79, 70, 229);
}
.dq-hero-blue {
  background: rgba(59, 130, 246, 0.14);
  color: rgb(37, 99, 235);
}

/* Body */
.dq-body {
  padding: 18px 22px 10px;
}

/* Summary bar */
.dq-summary {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  justify-content: space-between;
  padding: 12px 14px;
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(15, 23, 42, 0.02);
}
.dq-summary-title {
  font-weight: 950;
}
.dq-summary-sub {
  margin-top: 2px;
  color: rgba(15, 23, 42, 0.68);
  font-size: 0.86rem;
}
.dq-dot {
  margin: 0 8px;
  opacity: 0.5;
}
.dq-summary-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.dq-mini {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  font-weight: 800;
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.78);
}

/* Cards/sections */
.dq-card {
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 18px;
  background: #fff;
  overflow: hidden;
}
.dq-card-head {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 14px 14px 0 14px;
}
.dq-card-body {
  padding: 14px;
}
.dq-h6 {
  font-weight: 950;
  letter-spacing: -0.2px;
}
.dq-help {
  font-size: 0.8rem;
  color: rgba(15, 23, 42, 0.55);
  line-height: 1.35;
}

/* Badges */
.dq-badge {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}
.dq-badge-indigo {
  background: rgba(99, 102, 241, 0.12);
  color: rgb(79, 70, 229);
}
.dq-badge-blue {
  background: rgba(59, 130, 246, 0.12);
  color: rgb(37, 99, 235);
}
.dq-badge-amber {
  background: rgba(245, 158, 11, 0.16);
  color: rgb(180, 83, 9);
}
.dq-badge-purple {
  background: rgba(168, 85, 247, 0.12);
  color: rgb(126, 34, 206);
}

/* Inputs */
.dq-label {
  font-size: 0.82rem;
  font-weight: 900;
}
.dq-input {
  border-radius: 14px;
  padding: 10px 12px;
  transition: all 0.15s ease;
}
.dq-input:focus {
  border-color: rgba(99, 102, 241, 0.55);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.14);
}
.dq-hint {
  margin-top: 6px;
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.55);
}

/* Input group */
.dq-inputgroup .dq-addon {
  border-radius: 14px 0 0 14px;
  border-color: rgba(15, 23, 42, 0.08);
  background: rgba(15, 23, 42, 0.03);
}
.dq-inputgroup .dq-input {
  border-radius: 0 14px 14px 0;
}

/* Pills */
.dq-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  border-radius: 999px;
  font-weight: 950;
  font-size: 0.78rem;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
}
.dq-pill-live {
  background: rgba(34, 197, 94, 0.10);
  color: rgb(22, 163, 74);
  border-color: rgba(34, 197, 94, 0.18);
}
.dq-pill-amber {
  background: rgba(245, 158, 11, 0.12);
  color: rgb(180, 83, 9);
  border-color: rgba(245, 158, 11, 0.22);
}
.dq-pill-danger {
  background: rgba(239, 68, 68, 0.10);
  color: rgb(185, 28, 28);
  border-color: rgba(239, 68, 68, 0.18);
}
.dq-pill-draft {
  background: rgba(15, 23, 42, 0.05);
  color: rgba(15, 23, 42, 0.75);
}

/* Footer */
.dq-footer {
  padding: 14px 22px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
}
.dq-btn {
  border-radius: 12px;
  padding: 10px 16px;
  font-weight: 900;
}
.dq-btn-primary {
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.10);
}
</style>
